import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ChevronLeft, TrendingUp, ArrowUpCircle, Target, Zap, Trophy, Play } from "lucide-react";
import { WheelPicker, DELTA_STOPS, formatDelta } from "./WheelPicker";

const PLATES = [45, 35, 25, 10, 5, 2.5];
const roundTo5 = w => Math.round(w / 5) * 5;

const calcSetWeight = (cfg, max) => {
  if (!cfg?.percent_max || !max) return null;
  const pct = parseFloat(String(cfg.percent_max).replace("%", "").trim());
  const m = parseFloat(max);
  if (isNaN(pct) || pct <= 0 || isNaN(m) || m <= 0) return null;
  return roundTo5(m * (pct / 100));
};

const calcPlates = (total, barWeight) => {
  if (!barWeight || !total) return null;
  let perSide = Math.round(((total - barWeight) / 2) / 2.5) * 2.5;
  if (perSide <= 0) return null;
  const out = []; let rem = perSide;
  for (const pw of PLATES) { const c = Math.floor(rem / pw); if (c > 0) { out.push(`${pw}s×${c}`); rem -= c * pw; } }
  return out.join(", ");
};

const matchEquipment = (ex, equipmentList) => {
  if (ex.bar_equipment_id) {
    const m = equipmentList.find(eq => eq.equipment_id === ex.bar_equipment_id);
    if (m) return m;
  }
  const name = (ex.exercise_name || "").toLowerCase();
  const sorted = [...equipmentList].sort((a, b) => (b.name || "").length - (a.name || "").length);
  for (const eq of sorted) { if (eq.name && name.includes(eq.name.toLowerCase())) return eq; }
  return null;
};

const formatTime = (s) => {
  const m = Math.floor(s / 60); const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// ── Rest-timer ring around the weight number ──
const RestTimerRing = ({ totalSeconds, weight, lbsLabel, isActive, onSkip }) => {
  const [remaining, setRemaining] = useState(totalSeconds);
  useEffect(() => { setRemaining(totalSeconds); }, [totalSeconds]);
  useEffect(() => {
    if (!isActive || remaining <= 0) return;
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [isActive, remaining]);

  const R = 110;
  const C = 2 * Math.PI * R;
  const pct = totalSeconds > 0 ? remaining / totalSeconds : 0;
  const offset = C * (1 - pct);

  return (
    <div className="relative" style={{ width: 256, height: 256 }}>
      <svg viewBox="0 0 256 256" className="absolute inset-0">
        <circle cx="128" cy="128" r={R} fill="none" stroke="#1E293B" strokeWidth="8" />
        <circle cx="128" cy="128" r={R} fill="none"
                stroke={isActive ? (remaining <= 10 ? "#F59E0B" : "#3B82F6") : "#10B981"}
                strokeWidth="8" strokeLinecap="round"
                strokeDasharray={C} strokeDashoffset={isActive ? offset : 0}
                style={{ transform: "rotate(-90deg)", transformOrigin: "center", transition: "stroke-dashoffset 1s linear, stroke 0.4s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {isActive && remaining > 0 ? (
          <>
            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">Rest</span>
            <span className="text-5xl font-black text-white tabular-nums">{formatTime(remaining)}</span>
            <button onClick={onSkip} className="text-[10px] text-white/40 hover:text-white/80 mt-2 underline" data-testid="skip-rest-btn">Skip</button>
          </>
        ) : (
          <>
            <span className="text-7xl font-black text-white leading-none tabular-nums" style={{ letterSpacing: "-0.02em" }}>{weight ?? "—"}</span>
            <span className="text-sm text-white/40 mt-1">{lbsLabel}</span>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main full-screen overlay ──
export const FocusedWorkoutOverlay = ({
  exercises,           // flat [{...exercise, _sectionIndex, _exerciseIndex}, ...]
  userMaxes,
  equipmentList,
  exerciseLogs,        // resume data { "secIdx-exIdx": { sets, completed } }
  onUpdateLog,
  onUpdateMax,
  onSaveWeightIntent,
  onWorkoutComplete,
  onClose,
  isStrengthWorkout = true,
  isDeloadMode = false,
  videoMap = {},
}) => {
  // Find the first non-completed exercise from the resume data, else 0.
  const firstIncompleteIdx = useMemo(() => {
    for (let i = 0; i < exercises.length; i++) {
      const ex = exercises[i];
      const log = exerciseLogs?.[`${ex._sectionIndex}-${ex._exerciseIndex}`];
      if (!log?.completed) return i;
    }
    return 0;
  }, [exercises, exerciseLogs]);

  const [exIdx, setExIdx] = useState(firstIncompleteIdx);
  const [showWheel, setShowWheel] = useState(false);
  const [pendingDelta, setPendingDelta] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [restActive, setRestActive] = useState(false);
  const [allDone, setAllDone] = useState(false);

  const currentExercise = exercises[exIdx];
  const sectionIndex = currentExercise?._sectionIndex;
  const exerciseIndex = currentExercise?._exerciseIndex;
  const logKey = `${sectionIndex}-${exerciseIndex}`;
  const existingLog = exerciseLogs?.[logKey];

  const numSets = currentExercise?.sets || 3;
  const setsCfg = currentExercise?.sets_data || [];

  // Sets state for current exercise (loaded from log or fresh)
  const [setsData, setSetsData] = useState(() =>
    existingLog?.sets || Array.from({ length: numSets }, () => ({ weight: "", reps: "", completed: false }))
  );

  // Reset sets when current exercise changes
  useEffect(() => {
    const fresh = exerciseLogs?.[logKey]?.sets || Array.from({ length: numSets }, () => ({ weight: "", reps: "", completed: false }));
    setSetsData(fresh);
    setShowWheel(false);
    setPendingDelta(0);
    setRestActive(false);
  }, [exIdx, logKey, numSets]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSetIdx = setsData.findIndex(s => !s.completed);
  const allSetsComplete = currentSetIdx === -1;

  // Persist log upstream whenever sets change
  useEffect(() => {
    if (!currentExercise) return;
    onUpdateLog(sectionIndex, exerciseIndex, {
      exercise_id: currentExercise.exercise_id,
      exercise_name: currentExercise.exercise_name || "Exercise",
      sets: setsData,
      completed: allSetsComplete,
    });
  }, [setsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // When all sets done, surface the wheel picker (strength only)
  useEffect(() => {
    if (allSetsComplete && !showWheel && !allDone) {
      const isCategoryStrength = (currentExercise?.category || "").toLowerCase() === "strength" || isStrengthWorkout;
      if (isCategoryStrength) setShowWheel(true);
      else handleAdvance(); // non-strength → just move on
    }
  }, [allSetsComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!currentExercise) return null;

  const matchedEq = matchEquipment(currentExercise, equipmentList);
  const barWeight = matchedEq?.weight || null;
  const equipLabel = matchedEq ? `${matchedEq.name} (${matchedEq.weight} lbs)` : null;
  const userMax = userMaxes?.[currentExercise.exercise_id];

  // For "based-off" exercises, max is derived from another lift
  const basedOffId = currentExercise.based_off_exercise_id;
  const isDerivedMax = basedOffId && basedOffId !== currentExercise.exercise_id;
  const effectiveMax = isDerivedMax ? userMaxes?.[basedOffId] : userMax;

  const calcReps = (i) => {
    const cfg = setsCfg[i];
    let r = cfg?.reps || currentExercise.reps || "10";
    if (isDeloadMode && r) {
      const n = parseInt(r);
      if (!isNaN(n)) return Math.max(1, Math.ceil(n / 2)).toString();
    }
    return r;
  };

  const calcRestSec = (i) => {
    const cfg = setsCfg[i];
    if (cfg?.rest_mins) return Math.round(parseFloat(cfg.rest_mins) * 60);
    return currentExercise.rest_seconds || 60;
  };

  const setIdxToShow = allSetsComplete ? numSets - 1 : currentSetIdx;
  const programmedWeight = calcSetWeight(setsCfg[setIdxToShow], effectiveMax);
  const reps = calcReps(setIdxToShow);
  const plates = calcPlates(programmedWeight, barWeight);
  const restSec = calcRestSec(setIdxToShow);

  const handleSetComplete = () => {
    if (currentSetIdx === -1) return;
    setSetsData(prev => {
      const u = [...prev];
      const actual = u[currentSetIdx].weight || (programmedWeight ? programmedWeight.toString() : "");
      u[currentSetIdx] = { ...u[currentSetIdx], weight: actual, completed: true };
      return u;
    });
    // Trigger rest timer for next set (if any)
    const willHaveNext = currentSetIdx + 1 < numSets;
    if (willHaveNext) setRestActive(true);
  };

  const handleActualChange = (val) => {
    setSetsData(prev => {
      const u = [...prev];
      if (currentSetIdx >= 0) u[currentSetIdx] = { ...u[currentSetIdx], weight: val };
      return u;
    });
  };

  const applyDelta = (delta) => {
    if (onSaveWeightIntent) onSaveWeightIntent(currentExercise.exercise_id, delta);
    if (!isDerivedMax && delta !== 0 && onUpdateMax) {
      const cur = parseFloat(effectiveMax);
      if (!isNaN(cur) && cur > 0) {
        const newMax = Math.max(0, cur + delta);
        onUpdateMax(currentExercise.exercise_id, currentExercise.exercise_name, newMax);
      }
    }
  };

  const handleAdvance = () => {
    setShowWheel(false);
    if (exIdx + 1 < exercises.length) {
      setExIdx(exIdx + 1);
    } else {
      setAllDone(true);
    }
  };

  const handleConfirmDelta = () => {
    applyDelta(pendingDelta);
    handleAdvance();
  };

  const handleSkipRest = () => setRestActive(false);

  // ── Render ──
  const videoInfo = videoMap[currentExercise.exercise_id];
  const overallPct = exercises.length > 0 ? Math.round(((exIdx + (allSetsComplete ? 1 : 0)) / exercises.length) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-[#070B14] flex flex-col"
      data-testid="focused-workout-overlay">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-[#1E293B]/60">
        <button onClick={onClose} className="text-white/60 hover:text-white p-2 -ml-2" data-testid="focus-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 mx-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 text-center font-bold">Exercise {exIdx + 1} of {exercises.length}</div>
          <div className="h-1 bg-[#1E293B] rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${overallPct}%` }} />
          </div>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white p-2 -mr-2" data-testid="focus-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto px-5 pb-6 pt-3">

        {allDone ? (
          /* ── WORKOUT COMPLETE ── */
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="h-full flex flex-col items-center justify-center text-center py-12">
            <div className="w-24 h-24 rounded-full bg-success/20 flex items-center justify-center mb-6">
              <Trophy className="w-12 h-12 text-success" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2">Workout Complete!</h2>
            <p className="text-white/60 mb-8">Nice work — {exercises.length} exercises down.</p>
            <button onClick={onWorkoutComplete}
              className="bg-success hover:bg-success/90 text-black font-black px-8 py-4 rounded-2xl text-lg"
              data-testid="finish-workout-btn">
              Finish & Save
            </button>
          </motion.div>
        ) : showWheel ? (
          /* ── WHEEL PICKER (after last set) ── */
          <motion.div key="wheel" initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="space-y-4 max-w-md mx-auto w-full pt-6">
            <div className="text-center mb-3">
              <div className="inline-flex items-center gap-2 bg-success/15 border border-success/30 px-3 py-1.5 rounded-full mb-3">
                <Check className="w-4 h-4 text-success" />
                <span className="text-success font-bold text-xs">All {numSets} sets done</span>
              </div>
              <h3 className="text-xl font-bold text-white">{currentExercise.exercise_name}</h3>
            </div>
            <div className="bg-[#0B0F1A] border border-primary/30 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-primary" />
                <span className="text-sm font-bold text-white">Next time, I want to:</span>
              </div>
              <p className="text-xs text-white/40 mb-4">Spin the wheel — center value is selected.</p>
              <WheelPicker value={pendingDelta} onChange={setPendingDelta} testIdPrefix={`focus-delta-${exIdx}`} />
              <button onClick={handleConfirmDelta}
                className="w-full mt-4 bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl text-sm"
                data-testid={`focus-confirm-delta-${exIdx}`}>
                Confirm {formatDelta(pendingDelta)}
              </button>
              {!isDerivedMax && pendingDelta !== 0 && effectiveMax && (
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1.5 justify-center">
                  <ArrowUpCircle className="w-3.5 h-3.5" />
                  New max: <span className="font-bold">{Math.max(0, parseFloat(effectiveMax) + pendingDelta)} lbs</span>
                </p>
              )}
            </div>
          </motion.div>
        ) : (
          /* ── ACTIVE SET (focused) ── */
          <motion.div key={`set-${exIdx}-${currentSetIdx}`}
            initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="flex flex-col items-center max-w-md mx-auto w-full">

            {/* Exercise title */}
            <div className="text-center w-full mb-2">
              <div className="text-[10px] tracking-[0.2em] text-primary font-bold uppercase mb-1">
                Set {currentSetIdx + 1} of {numSets}
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-bold text-white leading-tight">{currentExercise.exercise_name}</h2>
                {videoInfo && (
                  <button onClick={() => setShowVideo(true)}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-400 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full"
                    data-testid="focus-watch-demo">
                    <Play className="w-2.5 h-2.5" /> Demo
                  </button>
                )}
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex gap-2 mb-6">
              {setsData.map((s, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${s.completed ? "bg-success" : i === currentSetIdx ? "bg-primary w-6" : "bg-[#374B69]"}`} />
              ))}
            </div>

            {/* Big ring + weight */}
            <RestTimerRing
              totalSeconds={currentSetIdx > 0 ? calcRestSec(currentSetIdx - 1) : restSec}
              weight={programmedWeight}
              lbsLabel={`lbs · × ${reps} rep${parseInt(reps) !== 1 ? "s" : ""}`}
              isActive={restActive}
              onSkip={handleSkipRest} />

            {/* Plates / equipment label */}
            {plates && <p className="text-sm text-emerald-400 mt-3 font-medium">Each side: {plates}</p>}
            {equipLabel && <p className="text-[11px] text-white/30 mt-1">{equipLabel}</p>}

            {/* Max input — compact */}
            {!isDerivedMax ? (
              <div className="mt-5 w-full flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <Target className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span className="text-sm text-amber-400 font-medium">Max:</span>
                <input type="number" placeholder="Enter" value={effectiveMax || ""}
                  onChange={(e) => onUpdateMax?.(currentExercise.exercise_id, currentExercise.exercise_name, parseFloat(e.target.value) || 0)}
                  className="flex-1 bg-[#0B0F1A] border border-amber-500/50 rounded-lg px-3 py-1.5 text-white text-sm focus:border-amber-400 outline-none max-w-[100px] text-center"
                  data-testid="focus-max-input" />
                <span className="text-xs text-amber-400/50">lbs</span>
              </div>
            ) : (
              <div className="mt-5 w-full flex items-center gap-2 p-2.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-sm">
                <span className="text-cyan-400">Based off {currentExercise.based_off_exercise_name} max:</span>
                <span className="text-white font-bold">{effectiveMax || "—"} lbs</span>
              </div>
            )}

            {/* Actual weight override */}
            <div className="mt-3 w-full flex items-center gap-2 bg-[#1E293B] border border-[#2D3B4F] rounded-lg px-3 py-2">
              <label className="text-xs text-white/40 flex-shrink-0">Actual:</label>
              <input type="number" placeholder={programmedWeight ? programmedWeight.toString() : "0"}
                value={setsData[currentSetIdx]?.weight || ""}
                onChange={(e) => handleActualChange(e.target.value)}
                className="flex-1 bg-transparent text-white text-sm text-center outline-none"
                data-testid="focus-actual-input" />
              <span className="text-xs text-white/30">lbs</span>
            </div>

            {/* Relax callout */}
            <div className="mt-4 w-full flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5">
              <Zap className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300 leading-snug">
                <span className="font-bold">Load weights first.</span> Then use the rest of the time to relax.
              </p>
            </div>

            {/* Set Complete CTA */}
            <button onClick={handleSetComplete}
              className="mt-5 w-full bg-success hover:bg-success/90 text-black font-black py-4 rounded-2xl text-base active:scale-[0.98] transition-transform shadow-lg shadow-success/20"
              data-testid="focus-set-complete-btn">
              <Check className="w-5 h-5 inline-block mr-2 -mt-0.5" />
              Set Complete
            </button>
          </motion.div>
        )}
      </div>

      {/* Video modal */}
      <AnimatePresence>
        {showVideo && videoInfo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-[#111827] border border-[#2D3B4F] rounded-2xl overflow-hidden max-w-2xl w-full" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b border-[#1E293B]">
                <h4 className="text-sm font-bold text-white">{videoInfo.title}</h4>
                <button onClick={() => setShowVideo(false)} className="text-gray-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="aspect-video">
                <iframe src={`https://www.youtube.com/embed/${videoInfo.youtube_id}?autoplay=1`} title={videoInfo.title}
                  className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FocusedWorkoutOverlay;
